/**
 * What an enrichment run's item rows actually say, counted client-side.
 *
 * **Why not `v_run_summary`.** Every counter on `RunSummaryRow` is keyed to a
 * scrape outcome: `created_count` is `final_outcome='created'` and
 * `duplicate_count` is `final_outcome='duplicate'`, neither of which an
 * enrichment job can ever be. A job settles `enriched` / `manual_review` /
 * `skipped` / `failed` / `cancelled` (Tina's `ENRICHMENT_OUTCOMES`), so the run
 * page rendered "Created 0 · 0%" for a run that enriched all fourteen of its
 * entities. Only `hitl_count` (`manual_review`), `skipped_count` and
 * `failed_count` carried over.
 *
 * **Why counting the rows is cheap enough to be the fix.** Measured against
 * prod on 2026-09-02: 4443 enrichment runs, mean 4.32 items, p95 **2**, max
 * 500 — so `listAllItems` is one request for all but a handful of runs and
 * three at the ceiling (page size 200). The show page already made that call
 * for the scrape "Created entities" panel. The alternative — an
 * `enriched_count` column on `v_run_summary` — is the right shape for a
 * million-row scrape run and pure overhead for a two-row job batch, and it
 * would have to ship through a Tina migration and deploy before IMS could read
 * it.
 *
 * Scrape runs are untouched: they keep the server-side summary, and nothing
 * here runs for them.
 */
import {
  ENRICHMENT_ENTITY_OPTIONS,
  enrichmentEntityHref,
  enrichmentEntityLabel,
  enrichmentModeLabel,
  enrichmentReviewHref,
  readEnrichmentJobRef,
} from "@/lib/tina/enrichment-jobs";
import type { FinalOutcome, PipelineItem } from "@/types/tina";

/** One entity a job row in this run was about. */
export interface EnrichmentRunEntity {
  entityType: string;
  entityId: string;
  label: string;
  /** The IMS page for the entity itself, or null where no route exists. */
  href: string | null;
  /** The unified enrichment page, `/{resource}/enrichment/:id`; null only without an id. */
  reviewHref: string | null;
  /** The `events` row that enriched it — the row a curator opens to debug. */
  itemId: string;
  modeLabel: string;
  outcome: FinalOutcome | null;
}

export interface EnrichmentRunEntityGroup {
  entityType: string;
  label: string;
  entities: EnrichmentRunEntity[];
}

export interface EnrichmentRunFacts {
  /** Job rows in the run. Equals `summary.event_count`; kept for a no-summary render. */
  total: number;
  enriched: number;
  /** `final_outcome='manual_review'` — the job gave up and is waiting on a human. */
  manualReview: number;
  /** `status='hitl'` — parked *right now*, and the only actionable state here. */
  parked: number;
  skipped: number;
  failed: number;
  /**
   * Rows that reached a verdict, i.e. carry any `final_outcome`.
   *
   * Deliberately not `total - in flight`: a `failed_retryable` row has no
   * outcome yet and is genuinely still running, and a parked row's outcome
   * arrives only when the curator answers.
   */
  settled: number;
  groups: EnrichmentRunEntityGroup[];
}

const ENTITY_ORDER: readonly string[] = ENRICHMENT_ENTITY_OPTIONS.map(
  (option) => option.value,
);

export function collectEnrichmentRunFacts(
  items: readonly PipelineItem[],
): EnrichmentRunFacts {
  const facts: EnrichmentRunFacts = {
    total: items.length,
    enriched: 0,
    manualReview: 0,
    parked: 0,
    skipped: 0,
    failed: 0,
    settled: 0,
    groups: [],
  };

  const groups = new Map<string, EnrichmentRunEntity[]>();
  const seen = new Set<string>();

  for (const item of items) {
    if (item.final_outcome) facts.settled += 1;
    if (item.status === "hitl") facts.parked += 1;
    switch (item.final_outcome) {
      case "enriched":
        facts.enriched += 1;
        break;
      case "manual_review":
        facts.manualReview += 1;
        break;
      case "skipped":
        facts.skipped += 1;
        break;
      case "failed":
        facts.failed += 1;
        break;
      default:
        break;
    }

    const ref = readEnrichmentJobRef(item);
    if (!ref) continue;
    // One entity can carry two job rows in the same run — a bypass fan-out
    // seeds `enrich` and `embed` separately. The panel is a list of entities,
    // not of rows, so the first row wins and the counters above (which are
    // per-row) stay the honest number of jobs.
    const key = `${ref.entity_type}:${ref.entity_id}`;
    if (seen.has(key)) continue;
    seen.add(key);

    let bucket = groups.get(ref.entity_type);
    if (!bucket) groups.set(ref.entity_type, (bucket = []));
    bucket.push({
      entityType: ref.entity_type,
      entityId: ref.entity_id,
      label: enrichmentEntityLabel(ref.entity_type),
      href: enrichmentEntityHref(ref.entity_type, ref.entity_id),
      reviewHref: enrichmentReviewHref(ref.entity_type, ref.entity_id),
      itemId: item.id,
      modeLabel: enrichmentModeLabel(ref.mode),
      outcome: item.final_outcome,
    });
  }

  facts.groups = Array.from(groups.entries())
    .map(([entityType, entities]) => ({
      entityType,
      label: enrichmentEntityLabel(entityType),
      entities,
    }))
    .sort((left, right) => order(left.entityType) - order(right.entityType));

  return facts;
}

/** Content-type order, with anything Tina grows after this deploy sorted last. */
function order(entityType: string): number {
  const index = ENTITY_ORDER.indexOf(entityType);
  return index === -1 ? ENTITY_ORDER.length : index;
}
